"use client";

import { useAppDispatch, useAppSelector } from "@/_hooks/hooks";
import { formatIndianNumber } from "@/_helper/helper";
import {
  setStep,
  setLoading,
  setCartItems,
  setHotelCart,
} from "@/store/cartSlice";
import {
  ChevronLeft,
  ArrowRight,
  ShieldCheck,
  CreditCard,
  Loader2,
  Calendar,
  Send,
} from "lucide-react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { motion } from "motion/react";
import { useState, useEffect, useMemo } from "react";
import { useEasebuzzPayment } from "@/hooks/useEasebuzzPayment";
import dynamic from "next/dynamic";
const ImportantNoticeModal = dynamic(() => import("./ImportantNoticeModal"), {
  ssr: false,
});
import {
  insertTempBooking,
  insertBooking,
  getStatesByCountry,
  getDistrictsByState,
  deleteCart,
} from "@/services/cart.services";
import { insertWebsiteInquiry } from "@/services/hotel.services";
import { useRouter } from "next/navigation";
import Link from "next/link";

const billingSchema = Yup.object().shape({
  FirstName: Yup.string().required("First name is required"),
  LastName: Yup.string().required("Last name is required"),
  Phone: Yup.string()
    .matches(
      /^(?!(\d)\1{9})[6-9]\d{9}$/,
      "Please enter a valid 10-digit mobile number",
    )
    .test(
      "no-sequential-spam",
      "Sequential patterns are not allowed",
      (val) => val !== "1234567890" && val !== "0987654321",
    )
    .required("Phone number is required"),
  WhatsAppMobileNo: Yup.string()
    .matches(
      /^(?!(\d)\1{9})[6-9]\d{9}$/,
      "Please enter a valid 10-digit WhatsApp number",
    )
    .test(
      "no-sequential-spam",
      "Sequential patterns are not allowed",
      (val) => val !== "1234567890" && val !== "0987654321",
    )
    .required("WhatsApp number is required"),
  Email: Yup.string().email("Invalid email").required("Email is required"),
  StateID: Yup.number()
    .required("State is required")
    .min(1, "Please select a state"),
  DistrictID: Yup.number()
    .required("District is required")
    .min(1, "Please select a district"),
  // DistrictName: Yup.string().required("District name is required"),
  Agreed: Yup.boolean().oneOf([true], "You must agree to the terms"),
  GSTNo: Yup.string().matches(
    /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/i,
    "Invalid GST No",
  ),
});

export default function Step3Payment() {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { initiatePayment } = useEasebuzzPayment();
  const { cartItems, hotelCart, visitors, ticketDate, sessionId } =
    useAppSelector((state) => state.cart);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGSTEnabled, setIsGSTEnabled] = useState(false);
  const [states, setStates] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [isImportantNoticeOpen, setIsImportantNoticeOpen] = useState(false);
  const [pendingValues, setPendingValues] = useState<any>(null);

  const isAnyHotelRoomUnavailable = useMemo(() => {
    if (!hotelCart || hotelCart.length === 0) return false;
    return hotelCart.some((hotel) => {
      const maxAvailable =
        hotel.AvailableRooms !== undefined ? hotel.AvailableRooms : 5;
      return maxAvailable <= 0 || (hotel.Rooms || 0) > maxAvailable;
    });
  }, [hotelCart]);

  useEffect(() => {
    const loadStates = async () => {
      try {
        const stateList = await getStatesByCountry();
        setStates(stateList);
      } catch (err) {
        console.error("Failed to load states:", err);
      }
    };
    loadStates();
  }, []);

  const ticketTotal = cartItems.reduce(
    (acc, item) => acc + (item.Total || 0),
    0,
  );
  const hotelTotal = hotelCart.reduce(
    (acc, item) => acc + (item.TotalAmount || 0),
    0,
  );

  const ticketServiceCharge = cartItems.reduce(
    (acc, item) => acc + (item.CalculatedServiceCharge || 0),
    0,
  );
  const hotelGST = hotelCart.reduce(
    (acc, item) => acc + (item.CalculatedGST || 0),
    0,
  );

  const totalServiceCharge = ticketServiceCharge + hotelGST;
  const rawSubTotal = ticketTotal + hotelTotal;
  const rawFinalPrice = rawSubTotal + totalServiceCharge;

  const finalTotal = Math.round(rawFinalPrice);
  const roundOff = Number((finalTotal - rawFinalPrice).toFixed(2));

  const formatDateForAPI = (dateStr: string) => {
    if (!dateStr) return "";
    if (dateStr.includes("-") && dateStr.split("-")[0].length === 4)
      return dateStr;
    if (dateStr.includes("/")) {
      const [d, m, y] = dateStr.split("/");
      return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }
    return dateStr;
  };

  const executePayment = async (values: any) => {
    try {
      setIsImportantNoticeOpen(false)
      setIsProcessing(true);

      if (isAnyHotelRoomUnavailable) {
        // Construct detailed inquiry payload
        const hotelDetailsStr = hotelCart
          .map(
            (item) =>
              `Hotel: ${item.HotelName}, Room Category: ${item.RoomCategoryName || "Standard"}, Meal Plan: ${item.MealPlanName || "None"}, CheckIn: ${item.CheckIn}, CheckOut: ${item.CheckOut}, Rooms: ${item.Rooms}, Adults: ${item.Adults}, Children: ${item.Child}`,
          )
          .join("\n");
        const ticketDetailsStr = cartItems
          .map(
            (item) =>
              `Ticket: ${item.TicketName}, Date: ${ticketDate || item.VisitDate || item.Date}, Quantity: ${(item.TotalAdults || 0) + (item.TotalChildSevenYr || 0) + (item.TotalChildThreeYr || 0)} (Adults: ${item.TotalAdults || 0}, Child 7-15: ${item.TotalChildSevenYr || 0}, Child 3-6: ${item.TotalChildThreeYr || 0})`,
          )
          .join("\n");
        const remarks = `Booking Inquiry (Rooms unavailable for direct booking):\n\n${hotelDetailsStr}\n\n${ticketDetailsStr}`;

        const inquiryPayload = {
          FirstName: `${(values.FirstName || "").toUpperCase().trim()} ${(values.LastName || "").toUpperCase().trim()}`.trim(),
          LastName: "",
          Email: values.Email,
          MobileNo: values.Phone,
          CheckInDate: hotelCart[0]?.CheckIn
            ? formatDateForAPI(hotelCart[0].CheckIn)
            : ticketDate
              ? formatDateForAPI(ticketDate)
              : new Date().toISOString().split("T")[0],
          CheckOut: hotelCart[0]?.CheckOut
            ? formatDateForAPI(hotelCart[0].CheckOut)
            : null,
          Adults:
            hotelCart[0]?.Adults ||
            visitors.filter((v: any) => v.Age > 15).length ||
            1,
          Child:
            hotelCart[0]?.Child ||
            visitors.filter((v: any) => v.Age <= 15).length ||
            0,
          HotelID: hotelCart[0]?.HotelID || null,
          TourURL: "",
          InquiryType: "Hotels",
          PackageName:
            hotelCart
              .map((h) => `${h.HotelName} (${h.Rooms} Rooms)`)
              .join(", ") || "Cart booking inquiry",
          WebsiteURL: typeof window !== "undefined" ? window.location.href : "",
          Remarks: remarks,
        };

        const response = await dispatch(insertWebsiteInquiry(inquiryPayload));
        if (response.meta.requestStatus === "fulfilled") {
          // Clear cart items from DB
          setIsProcessing(false);
          router.push("/thankyou/");
          try {
            await Promise.all([
              ...cartItems.map((item) => deleteCart(item.CartID, "Ticket")),
              ...hotelCart.map((item) => deleteCart(item.CartID, "Hotel")),
            ]);
          } catch (clearErr) {
            console.error("Failed to clear cart DB:", clearErr);
          }

          dispatch(setCartItems([]));
          dispatch(setHotelCart([]));
          dispatch(setStep(0));
        } else {
          throw new Error("Failed to send inquiry. Please try again.");
        }
        return;
      }

      // 1. Create Temp Booking
      const tempPayload = {
        SessionID: sessionId || "",
        FirstName: (values.FirstName || "").toUpperCase().trim(),
        LastName: (values.LastName || "").toUpperCase().trim(),
        MobileNo: values.Phone,
        WhatsAppMobileNo: values.WhatsAppMobileNo,
        EmailID: values.Email,
        StateID: Number(values.StateID) || 0,
        DistrictID: Number(values.DistrictID) || 0,
        DistrictName: values.DistrictName || "",
        GSTNo: values.GSTNo || "",
        CompanyName: values.CompanyName || "",
        CompanyAddress: values.CompanyAddress || "",
        Price: rawSubTotal,
        ServiceCharge: totalServiceCharge,
        FinalPrice: finalTotal,
        RoundOff: roundOff,
        TotalRooms: hotelCart.reduce((acc, item) => acc + (item.Rooms || 0), 0),
        TotalTickets: cartItems.length,
        TicketDate: ticketDate || null,
        TicketCart: cartItems.map((item) => ({
          TicketID: item.TicketID,
          TimeSlotID: item.ViewingGallerySlotID || 0,
          ProjectionMappingID:
            item.ProjectionMappingSlotID === 0
              ? null
              : item.ProjectionMappingSlotID || 0,
          Price: item.BaseTotal || item.Total,
          ServiceCharge: item.CalculatedServiceCharge || 0,
          TotalPrice: item.Total + item.CalculatedServiceCharge || 0,
          NoOfTicket:
            (item.TotalAdults || 0) +
            (item.TotalChildSevenYr || 0) +
            (item.TotalChildThreeYr || 0),
          TotalAdults: item.TotalAdults || 0,
          TotalChildSevenYr: item.TotalChildSevenYr || 0,
          TotalChildThreeYr: item.TotalChildThreeYr || 0,
          TicketPaxDetails: [],
          SlotIDs: item.SlotIDs || "",
        })),
        HotelCart: hotelCart.map((item) => ({
          HotelID: item.HotelID,
          RoomCategoryID: item.RoomCategoryID,
          GSTPrice: item.CalculatedGST,
          MealPlanID: item.MealPlanID,
          CheckIn: formatDateForAPI(item.CheckIn),
          CheckOut: formatDateForAPI(item.CheckOut),
          Night: item.Night,
          Rooms: item.Rooms,
          Adults: item.Adults,
          Child: item.Child,
          ExtraAdult: item.ExtraAdult || 0,
          ExtraChild: item.ExtraChild || 0,
          TotalAmount: item.TotalAmount + item.CalculatedGST,
        })),
      };

      const tempRes = await insertTempBooking(tempPayload);
      if (tempRes.Status !== 201) {
        throw new Error(
          tempRes.Message || "Failed to create temporary booking",
        );
      }

      const tempId = tempRes.Data?.TempID || tempRes.TempID;
      const BookingNo = tempRes.Data?.BookingNo || tempRes.BookingNo;

      // 2. Initiate Easebuzz Payment Iframe Checkout Flow
      const paymentResult = await initiatePayment({
        tempBookingId: String(tempId),
        amount: finalTotal,
        productInfo: `Booking ${BookingNo}`,
        firstname: `${(values.FirstName || "").toUpperCase().trim()} ${(values.LastName || "").toUpperCase().trim()}`.trim(),
        email: values.Email,
        phone: values.Phone,
      });

      if (paymentResult.success) {
        try {
          // 3. Finalize Booking
          const res = await insertBooking({ TempID: Number(tempId) });
          const finalBookingId = res?.Data?.ID || res?.Data?.Id || tempId;

          dispatch(setCartItems([]));
          dispatch(setHotelCart([]));
          dispatch(setStep(0));
          setIsProcessing(false);
          sessionStorage.setItem("bookingId", String(finalBookingId));
          router.push("/booking-success");
        } catch (finalizeErr: any) {
          setIsProcessing(false);
          toast.error(finalizeErr.message || "Failed to finalize booking");
        }
      } else {
        setIsProcessing(false);
        if (paymentResult.status === "userCancelled") {
          toast.error("Payment checkout process cancelled by user.");
        } else {
          toast.error(
            paymentResult.status === "error"
              ? "Something went wrong during payment."
              : "Payment verification failed.",
          );
        }
      }
    } catch (error: any) {
      toast.error(error.message || "Something went wrong during checkout");
      setIsProcessing(false);
    }
  };

  const handleSubmit = async (values: any) => {
    if (isAnyHotelRoomUnavailable) {
      await executePayment(values);
    } else {
      setPendingValues(values);
      setIsImportantNoticeOpen(true);
    }
  };

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in duration-500 pb-16 relative">
      {/* Processing Overlay */}
      {isProcessing && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-[9999] flex flex-col items-center justify-center">
          <div className="bg-white p-8 rounded-3xl shadow-2xl flex flex-col items-center gap-6 border border-orange-100">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-orange-50 rounded-full"></div>
              <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
            </div>
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Processing Your Booking
              </h3>
              <p className="text-gray-500 text-sm">
                Please do not refresh or close the window...
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-12 gap-4 sm:gap-6 lg:gap-8 items-start">
        {/* Left Side: Billing Details */}
        <div className="lg:col-span-7 space-y-4 sm:space-y-8">
          <div className="bg-white rounded-2xl sm:rounded-[32px] border border-orange-50 shadow-sm p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
            <div>
              <h2 className="text-xl sm:text-2xl font-semibold text-gray-900">
                Billing <span className="text-orange-600">details</span>
              </h2>
              <p className="text-gray-400 text-sm font-medium italic leading-relaxed">
                Confirm your information to complete the booking.
              </p>
            </div>

            <Formik
              initialValues={{
                FirstName: (visitors[0]?.Name || "").toUpperCase(),
                LastName: "",
                Phone: "",
                WhatsAppMobileNo: "",
                Email: "",
                StateID: "",
                DistrictName: "",
                GSTNo: "",
                CompanyName: "",
                CompanyAddress: "",
                Agreed: false,
              }}
              validationSchema={billingSchema}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue, handleSubmit: formikSubmit }) => (
                <Form className="space-y-6 sm:space-y-10">
                  <div className="grid md:grid-cols-2 gap-x-4 sm:gap-x-8 gap-y-3">
                    {/* First Name */}
                    <div className="col-span-2 sm:col-span-1">
                      <label className="text-xs font-semibold text-gray-600 ml-1">
                        First name
                      </label>
                      <Field
                        name="FirstName"
                        placeholder="Enter first name"
                        className="w-full px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl border border-gray-100 bg-[#FCF8F5] text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-orange-500 outline-none transition-all shadow-sm text-sm uppercase"
                      />
                      <ErrorMessage
                        name="FirstName"
                        component="div"
                        className="text-[10px] text-orange-600 font-medium px-1 mt-1"
                      />
                    </div>

                    {/* Last Name */}
                    <div className="col-span-2 sm:col-span-1">
                      <label className="text-xs font-semibold text-gray-600 ml-1">
                        Last name
                      </label>
                      <Field
                        name="LastName"
                        placeholder="Enter last name"
                        className="w-full px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl border border-gray-100 bg-[#FCF8F5] text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-orange-500 outline-none transition-all shadow-sm text-sm uppercase"
                      />
                      <ErrorMessage
                        name="LastName"
                        component="div"
                        className="text-[10px] text-orange-600 font-medium px-1 mt-1"
                      />
                    </div>

                    {/* Phone Number */}

                    <div className="col-span-2 sm:col-span-1">
                      <label className="text-xs font-semibold text-gray-600 ml-1 block mb-1">
                        <span className="h-5 flex items-center">
                          Phone number
                        </span>
                      </label>
                      <Field
                        name="Phone"
                        placeholder="Enter phone number"
                        maxLength={10}
                        minLength={10}
                        className="w-full px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl border border-gray-100 bg-[#FCF8F5] text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-orange-500 outline-none transition-all shadow-sm text-sm"
                      />
                      <ErrorMessage
                        name="Phone"
                        component="div"
                        className="text-[10px] text-orange-600 font-medium px-1 mt-1"
                      />
                    </div>

                    <div className="col-span-2 sm:col-span-1">
                      <label className="text-xs font-semibold text-gray-600 ml-1 block mb-1">
                        <span className="h-5 flex justify-between items-center">
                          <span>WhatsApp Mobile No.</span>
                          <button
                            type="button"
                            onClick={() =>
                              setFieldValue("WhatsAppMobileNo", values.Phone)
                            }
                            className="text-[10px] text-orange-600 hover:underline uppercase tracking-wide font-bold cursor-pointer"
                          >
                            Same as Mobile
                          </button>
                        </span>
                      </label>
                      <Field
                        name="WhatsAppMobileNo"
                        placeholder="Enter WhatsApp mobile no."
                        maxLength={10}
                        minLength={10}
                        className="w-full px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl border border-gray-100 bg-[#FCF8F5] text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-orange-500 outline-none transition-all shadow-sm text-sm"
                      />
                      <ErrorMessage
                        name="WhatsAppMobileNo"
                        component="div"
                        className="text-[10px] text-orange-600 font-medium px-1 mt-1"
                      />
                    </div>

                    {/* Email */}
                    <div className="col-span-2">
                      <label className="text-xs font-semibold text-gray-600 ml-1">
                        Email address
                      </label>
                      <Field
                        name="Email"
                        type="email"
                        placeholder="Enter email address"
                        className="w-full px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl border border-gray-100 bg-[#FCF8F5] text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-orange-500 outline-none transition-all shadow-sm text-sm"
                      />
                      <ErrorMessage
                        name="Email"
                        component="div"
                        className="text-[10px] text-orange-600 font-medium px-1 mt-1"
                      />
                    </div>

                    {/* State */}
                    <div>
                      <label className="text-xs font-semibold text-gray-600 ml-1">
                        State
                      </label>
                      <Field
                        as="select"
                        name="StateID"
                        onChange={async (e: any) => {
                          const val = e.target.value;
                          setFieldValue("StateID", val);
                          setFieldValue("DistrictID", "");
                          if (val) {
                            try {
                              const distList = await getDistrictsByState(
                                Number(val),
                              );
                              setDistricts(distList);
                            } catch (err) {
                              setDistricts([]);
                              console.error("Failed to load districts:", err);
                            }
                          } else {
                            setDistricts([]);
                          }
                        }}
                        className="w-full px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl border border-gray-100 bg-[#FCF8F5] text-gray-800 focus:ring-1 focus:ring-orange-500 outline-none transition-all shadow-sm text-sm cursor-pointer"
                      >
                        <option value="">Select State</option>
                        {states.map((st: any) => (
                          <option key={st.ID} value={st.ID}>
                            {st.StateName}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="StateID"
                        component="div"
                        className="text-[10px] text-orange-600 font-medium px-1 mt-1"
                      />
                    </div>

                    {/* District */}
                    {/* <div>
                      <label className="text-xs font-semibold text-gray-600 ml-1">
                        District
                      </label>
                      <Field
                        name="DistrictName"
                        placeholder={`Enter District name`}
                        // disabled={!values.StateID || districts.length === 0}
                        className="w-full px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl border border-gray-100 bg-[#FCF8F5] text-gray-800 focus:ring-1 focus:ring-orange-500 outline-none transition-all shadow-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <ErrorMessage
                        name="DistrictName"
                        component="div"
                        className="text-[10px] text-orange-600 font-medium px-1 mt-1"
                      />
                    </div> */}
                    <div className="w-full">
                      <label className="text-xs font-semibold text-gray-600 ml-1">
                        District
                      </label>
                      <Field
                        as="select"
                        name="DistrictID"
                        disabled={!values.StateID || districts.length === 0}
                        className="w-full px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl border border-gray-100 bg-[#FCF8F5] text-gray-800 focus:ring-1 focus:ring-orange-500 outline-none transition-all shadow-sm text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="">Select District</option>
                        {districts.map((dst: any) => (
                          <option key={dst.ID} value={dst.ID}>
                            {dst.Name || dst.DistrictName}
                          </option>
                        ))}
                      </Field>
                      <ErrorMessage
                        name="DistrictID"
                        component="div"
                        className="text-[10px] text-orange-600 font-medium px-1 mt-1"
                      />
                    </div>
                  </div>

                  {/* Optional GST Section */}
                  {!isAnyHotelRoomUnavailable && (
                    <div className="pt-4 space-y-4">
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <div className="relative flex items-center">
                          <input
                            type="checkbox"
                            checked={isGSTEnabled}
                            onChange={(e) => setIsGSTEnabled(e.target.checked)}
                            className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border border-slate-300 transition-all checked:border-orange-500 checked:bg-orange-500"
                          />
                          <span className="absolute text-white opacity-0 peer-checked:opacity-100 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-3.5 w-3.5"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                              stroke="currentColor"
                              strokeWidth="1"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              ></path>
                            </svg>
                          </span>
                        </div>
                        <span className="text-sm font-bold text-gray-700 select-none">
                          GST (Optional)
                        </span>
                      </label>

                      {isGSTEnabled && (
                        <div className="space-y-4 sm:space-y-6 animate-in fade-in slide-in-from-top-2 duration-300">
                          <div className="flex items-center gap-3">
                            <div className="h-px flex-1 bg-gray-100"></div>
                            <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] px-2">
                              GST Details
                            </h3>
                            <div className="h-px flex-1 bg-gray-100"></div>
                          </div>

                          <div className="grid md:grid-cols-2 gap-x-4 sm:gap-x-8 gap-y-3">
                            <div>
                              <label className="text-xs font-semibold text-gray-600 ml-1">
                                GST Number
                              </label>
                              <Field
                                name="GSTNo"
                                placeholder="Ex: 24AAAAA0000A1Z5"
                                className="w-full px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl border border-gray-100 bg-[#FCF8F5] text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-orange-500 outline-none transition-all shadow-sm text-sm"
                              />
                              <ErrorMessage
                                name="GSTNo"
                                component="div"
                                className="text-[10px] text-orange-600 font-medium px-1 mt-1"
                              />
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-gray-600 ml-1">
                                Company Name
                              </label>
                              <Field
                                name="CompanyName"
                                placeholder="Enter company name"
                                className="w-full px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl border border-gray-100 bg-[#FCF8F5] text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-orange-500 outline-none transition-all shadow-sm text-sm"
                              />
                              <ErrorMessage
                                name="CompanyName"
                                component="div"
                                className="text-[10px] text-orange-600 font-medium px-1 mt-1"
                              />
                            </div>
                            <div className="md:col-span-2 space-y-1">
                              <label className="text-xs font-semibold text-gray-600 ml-1">
                                Company Address
                              </label>
                              <Field
                                name="CompanyAddress"
                                as="textarea"
                                rows={2}
                                placeholder="Enter registered company address"
                                className="w-full px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl border border-gray-100 bg-[#FCF8F5] text-gray-800 placeholder-gray-400 focus:ring-1 focus:ring-orange-500 outline-none transition-all shadow-sm text-sm resize-none"
                              />
                              <ErrorMessage
                                name="CompanyAddress"
                                component="div"
                                className="text-[10px] text-orange-600 font-medium px-1 mt-1"
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* <div className="pt-6 space-y-4">
                    <h3 className="text-base font-semibold text-gray-900 border-l-2 border-orange-500 pl-3 tracking-tight">Important information</h3>
                    <div className="flex flex-wrap gap-6 text-[10px] font-semibold tracking-widest text-gray-400 uppercase">
                      <span className="hover:text-orange-600 cursor-pointer transition-colors border-b border-transparent hover:border-orange-500 pb-0.5">Privacy policy</span>
                      <span className="hover:text-orange-600 cursor-pointer transition-colors border-b border-transparent hover:border-orange-500 pb-0.5">Cancellation policy</span>
                      <span className="hover:text-orange-600 cursor-pointer transition-colors border-b border-transparent hover:border-orange-500 pb-0.5">Refund policy</span>
                    </div>
                  </div> */}

                  {/* Agree Checkbox inside Form */}
                  <div className="pt-4">
                    <label className="flex items-start gap-4 cursor-pointer group select-none">
                      <div className="relative flex items-center h-5">
                        <Field
                          type="checkbox"
                          name="Agreed"
                          className="w-5 h-5 rounded-md border-gray-200 text-orange-500 focus:ring-orange-500 cursor-pointer transition-all hover:border-orange-300 shadow-sm"
                        />
                      </div>
                      <div className="text-xs text-gray-500 leading-normal font-medium">
                        By checking this box, you are agreeing to our
                        <Link
                          href="/terms-conditions"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-orange-600 font-bold hover:text-orange-700 transition-colors mx-1"
                        >
                          Terms & Conditions
                        </Link>
                        and
                        <Link
                          href="/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-orange-600 font-bold hover:text-orange-700 transition-colors mx-1"
                        >
                          Privacy Policy
                        </Link>
                        .
                      </div>
                    </label>
                    <ErrorMessage
                      name="Agreed"
                      component="div"
                      className="text-[11px] text-red-500 font-semibold mt-2 ml-9"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isProcessing}
                    className="w-full bg-gradient-to-r from-orange-500 to-amber-600 text-white font-semibold py-4 rounded-xl flex items-center justify-center gap-3 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all group disabled:opacity-50"
                  >
                    <span className="text-base tracking-wider uppercase">
                      {isAnyHotelRoomUnavailable ? "Proceed" : "Make payment"}
                    </span>
                    <div className="bg-white/20 p-1 rounded-full group-hover:translate-x-1 transition-transform">
                      {isAnyHotelRoomUnavailable ? (
                        <Send className="w-4 h-4" />
                      ) : (
                        <ArrowRight className="w-4 h-4" />
                      )}
                    </div>
                  </button>
                  <div className="fixed bottom-0 left-0 right-0 z-[999] md:hidden z-[9999] bg-white p-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-semibold text-gray-900 tracking-tight">
                        {isAnyHotelRoomUnavailable ? "Total" : "Total payable"}
                      </span>
                      <span className="text-2xl font-semibold text-orange-600 tracking-tight italic">
                        ₹ {formatIndianNumber(finalTotal)}
                      </span>
                    </div>
                    <button
                      type="submit"
                      disabled={isProcessing}
                      className="mt-4 w-full bg-gradient-to-r from-orange-500 to-amber-600 text-white py-3 rounded-lg font-bold hover:bg-green-800 transition-all shadow-lg hover:shadow-xl active:scale-98 disabled:bg-gray-400 flex items-center justify-center gap-2 text-sm disabled:opacity-50"
                    >
                      {isProcessing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <span className="text-base tracking-wider uppercase">
                            {isAnyHotelRoomUnavailable
                              ? "Proceed"
                              : "Proceed to Payment"}
                          </span>
                          <div className="bg-white/20 p-1 rounded-full group-hover:translate-x-1 transition-transform">
                            {isAnyHotelRoomUnavailable ? (
                              <Send className="w-4 h-4" />
                            ) : (
                              <ArrowRight className="w-4 h-4" />
                            )}
                          </div>
                        </>
                      )}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>

          <div className="flex items-center gap-3 p-5 bg-green-50 rounded-2xl border border-green-100 text-green-700">
            <ShieldCheck className="w-6 h-6 shrink-0" />
            <p className="text-xs font-medium">
              Your connection is encrypted and your payment information is 100%
              secure.
            </p>
          </div>
        </div>

        {/* Right Side: Booking Summary Card */}
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#FFF9F6] rounded-2xl sm:rounded-[32px] border border-orange-100 p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8 shadow-md relative overflow-hidden"
          >
            <div className="space-y-1 relative">
              <h3 className="text-xl font-semibold text-gray-900 tracking-tight">
                Booking <span className="text-orange-600">summary</span>
              </h3>
              <p className="text-[9px] text-gray-400 font-semibold tracking-widest">
                "Secure your experience now"
              </p>
            </div>

            <div className="space-y-4 sm:space-y-5">
              <div className="flex items-center justify-between">
                <h4 className="text-[10px] font-semibold text-orange-600 tracking-widest uppercase">
                  Your Selection
                </h4>
                <span className="px-2 py-0.5 bg-white rounded-full text-[9px] font-semibold text-gray-400 border border-orange-50">
                  {cartItems.length + hotelCart.length} items
                </span>
              </div>
              <div className="space-y-3 sm:space-y-4">
                {/* Hotels */}
                {hotelCart.map((item: any) => (
                  <div
                    key={item.CartID || item.ID}
                    className="flex justify-between items-start gap-4 group"
                  >
                    <div className="flex items-start gap-2.5 flex-1">
                      <div className="w-1 h-1 rounded-full bg-blue-200 mt-1.5 shrink-0 group-hover:bg-blue-500 transition-colors"></div>
                      <div className="space-y-0.5">
                        <p className="text-xs font-semibold text-gray-700 leading-snug">
                          {item.HotelName}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-[8px] font-bold border border-blue-100 uppercase tracking-tighter">
                            {item.Rooms} Room
                          </span>
                          <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 text-[8px] font-bold border border-blue-100 uppercase tracking-tighter">
                            {item.Adults} Adults
                          </span>
                          <span className="px-1.5 py-0.5 rounded bg-white text-gray-500 text-[8px] font-bold border border-gray-100 uppercase tracking-tighter flex items-center gap-1">
                            <Calendar className="w-2 h-2" />
                            {new Date(item.CheckIn).toLocaleDateString()} -{" "}
                            {new Date(item.CheckOut).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <span className="font-semibold text-gray-900 whitespace-nowrap text-xs">
                      ₹ {formatIndianNumber(item.TotalAmount || 0)}
                    </span>
                  </div>
                ))}
                {/* Tickets */}
                {cartItems.map((item: any) => (
                  <div
                    key={item.CartID || item.ID}
                    className="flex justify-between items-start gap-4 group"
                  >
                    <div className="flex items-start gap-2.5 flex-1">
                      <div className="w-1 h-1 rounded-full bg-orange-200 mt-1.5 shrink-0 group-hover:bg-orange-500 transition-colors"></div>
                      <div className="space-y-0.5">
                        <p className="text-xs font-semibold text-gray-700 leading-snug">
                          {item.TicketName}
                        </p>
                        <div className="flex flex-wrap gap-1.5 mt-1.5">
                          {item.TotalAdults > 0 && (
                            <span className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-600 text-[8px] font-bold border border-orange-100 uppercase tracking-tighter">
                              {item.TotalAdults} Adult
                            </span>
                          )}
                          {item.TotalChildSevenYr > 0 && (
                            <span className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-600 text-[8px] font-bold border border-orange-100 uppercase tracking-tighter">
                              {item.TotalChildSevenYr} Child (3-15)
                            </span>
                          )}
                          {item.TotalChildThreeYr > 0 && (
                            <span className="px-1.5 py-0.5 rounded bg-orange-50 text-orange-600 text-[8px] font-bold border border-orange-100 uppercase tracking-tighter">
                              {item.TotalChildThreeYr} Child (3-6)
                            </span>
                          )}
                          {ticketDate && (
                            <span className="px-1.5 py-0.5 rounded bg-white text-gray-500 text-[8px] font-bold border border-gray-100 uppercase tracking-tighter flex items-center gap-1">
                              <Calendar className="w-2 h-2" />
                              {new Date(ticketDate).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className="font-semibold text-gray-900 whitespace-nowrap text-xs">
                      ₹ {formatIndianNumber(item.Total || 0)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3 sm:space-y-4 pt-4 sm:pt-6 border-t border-orange-100 border-dashed">
              <div className="flex justify-between items-center text-gray-600">
                <span className="text-[10px] font-semibold tracking-widest uppercase">
                  Sub Total
                </span>
                <span className="font-semibold text-gray-900 text-xs">
                  ₹ {formatIndianNumber(rawSubTotal)}
                </span>
              </div>

              {totalServiceCharge > 0 && (
                <div className="flex justify-between items-center text-gray-600">
                  <span className="text-[10px] font-semibold tracking-widest uppercase">
                    Service Charge + GST (incl.)
                  </span>
                  <span className="font-semibold text-gray-900 text-xs">
                    ₹ {formatIndianNumber(totalServiceCharge)}
                  </span>
                </div>
              )}

              {roundOff !== 0 && (
                <div className="flex justify-between items-center text-gray-600">
                  <span className="text-[10px] font-semibold tracking-widest uppercase">
                    Round Off
                  </span>
                  <span className="font-semibold text-gray-900 text-xs">
                    ₹{" "}
                    {roundOff > 0
                      ? `+${roundOff.toFixed(2)}`
                      : roundOff.toFixed(2)}
                  </span>
                </div>
              )}

              <div className="flex justify-between items-center pt-4 border-t border-orange-100">
                <span className="text-lg font-semibold text-gray-900 tracking-tight">
                  {isAnyHotelRoomUnavailable ? "Total" : "Total payable"}
                </span>
                <span className="text-2xl font-semibold text-orange-600 tracking-tight italic">
                  ₹ {formatIndianNumber(finalTotal)}
                </span>
              </div>
            </div>
          </motion.div>

          <button
            onClick={() => dispatch(setStep(0))}
            className="mt-4 sm:mt-8 mx-auto flex items-center gap-2 text-gray-400 font-semibold hover:text-orange-600 transition-all text-[9px] tracking-widest group uppercase"
          >
            <ChevronLeft className="w-3.5 h-3.5 group-hover:-translate-x-1 transition-transform" />
            <span>Review attractions</span>
          </button>
        </div>
      </div>
      <ImportantNoticeModal
        isOpen={isImportantNoticeOpen}
        onClose={() => setIsImportantNoticeOpen(false)}
        onConfirm={() => executePayment(pendingValues)}
      />
    </div>
  );
}
